import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import type { PiekAnalyse, AnalysisConfig, AnalysisResult, QuarterData } from '@/types/piekanalyse';

export function usePiekanalyses() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: analyses = [], isLoading, error } = useQuery({
    queryKey: ['piek-analyses', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('piek_analyses')
        .select(`
          *,
          organizations!piek_analyses_organization_id_fkey(name)
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Map organization name to the analysis object
      return (data || []).map((item: any) => ({
        ...item,
        organization_name: item.organizations?.name || null,
      })) as unknown as PiekAnalyse[];
    },
    enabled: !!user?.id,
  });

  const createAnalysis = useMutation({
    mutationFn: async ({ 
      name, 
      config, 
      result,
      organizationId,
      analysisYear,
    }: { 
      name: string; 
      config: AnalysisConfig; 
      result: AnalysisResult;
      organizationId?: string;
      analysisYear?: number;
    }) => {
      if (!user?.id) throw new Error('Not authenticated');
      
      const { data, error } = await supabase
        .from('piek_analyses')
        .insert({
          tenant_id: user.id,
          name,
          config,
          year_overview: result.yearOverview,
          monthly_overviews: result.monthlyOverviews,
          highest_day: {
            date: result.highestDay.date.toISOString(),
            hourlyData: result.highestDay.hourlyData,
          },
          highest_peak_kwp: result.highestPeakKWp,
          months_above_contract: result.monthsAboveContract,
          total_exceedances: result.totalExceedances,
          has_return_delivery: result.hasReturnDelivery,
          return_delivery_year_overview: result.returnDeliveryYearOverview,
          return_delivery_monthly_overviews: result.returnDeliveryMonthlyOverviews,
          return_delivery_highest_day: result.returnDeliveryHighestDay,
          // New fields
          organization_id: organizationId || null,
          analysis_year: analysisYear || result.detectedYear || null,
          raw_data: result.rawData || null,
        } as any)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['piek-analyses'] });
      toast.success('Analyse opgeslagen');
    },
    onError: (error) => {
      console.error('Create analysis error:', error);
      toast.error('Kon analyse niet opslaan');
    },
  });

  const deleteAnalysis = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('piek_analyses')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['piek-analyses'] });
      toast.success('Analyse verwijderd');
    },
    onError: (error) => {
      console.error('Delete analysis error:', error);
      toast.error('Kon analyse niet verwijderen');
    },
  });

  return {
    analyses,
    isLoading,
    error,
    createAnalysis,
    deleteAnalysis,
  };
}
